import { getApolloClient } from '@choco-admin/graphql-client';
import {
  EnvironmentKey,
  getSelectedEnvironment,
  setSelectedEnvironment,
} from '@choco-admin/utils';
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';

interface EnvironmentContextType {
  currentEnvironment: EnvironmentKey;
  switchEnvironment: (env: EnvironmentKey) => Promise<void>;
  isLoading: boolean;
}

const EnvironmentContext = createContext<EnvironmentContextType | undefined>(
  undefined
);

interface EnvironmentProviderProps {
  children: React.ReactNode;
}

export const EnvironmentProvider: React.FC<EnvironmentProviderProps> = ({
  children,
}) => {
  const [currentEnvironment, setCurrentEnvironment] = useState<EnvironmentKey>(
    getSelectedEnvironment
  );
  const [isLoading, setIsLoading] = useState(false);

  const switchEnvironment = useCallback(
    async (newEnvironment: EnvironmentKey) => {
      if (newEnvironment === currentEnvironment) {
        return;
      }

      setIsLoading(true);

      try {
        // Save the new environment selection
        setSelectedEnvironment(newEnvironment);

        // Force logout and reload the page to reinitialize with new environment
        // This simulates the legacy system behavior
        if (
          // eslint-disable-next-line no-alert
          window.confirm(
            `Switch to ${newEnvironment} environment? This will log you out and reload the page.`
          )
        ) {
          // Sign out current user and clear caches before switching pools
          try {
            const { signOut } = await import('@aws-amplify/auth');
            await signOut();
          } catch (error) {
            // eslint-disable-next-line no-console
            console.warn(
              'Could not sign out before environment switch:',
              error
            );
          }

          // Clear Apollo cache so no stale data from the old user pool leaks
          try {
            const client = getApolloClient();
            if (client) {
              await client.clearStore();
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Could not clear Apollo cache:', error);
          }

          // Reload the page to reinitialize with new environment
          window.location.reload();
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to switch environment:', error);
        setIsLoading(false);
      }
    },
    [currentEnvironment]
  );

  // Update current environment if it changes in localStorage (from other tabs)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'ChocoAdminSelectedEnvironment' && e.newValue) {
        const newEnv = e.newValue as EnvironmentKey;
        if (newEnv !== currentEnvironment) {
          setCurrentEnvironment(newEnv);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [currentEnvironment]);

  const value = {
    currentEnvironment,
    switchEnvironment,
    isLoading,
  };

  return (
    <EnvironmentContext.Provider value={value}>
      {children}
    </EnvironmentContext.Provider>
  );
};

export const useEnvironment = (): EnvironmentContextType => {
  const context = useContext(EnvironmentContext);
  if (context === undefined) {
    throw new Error(
      'useEnvironment must be used within an EnvironmentProvider'
    );
  }
  return context;
};
